// THE LIFECYCLE CONSOLE — one engagement, one verdict, one gate.
//
// The page answers a single question, in its first line: can this engagement leave the phase it
// is in, and if not, what exactly is holding it. Everything else is subordinate to that sentence.
//
// What the study changed, and why (scratchpad ui-rethink/tab-lifecycle):
//
//   · The gate engine now NAMES the rows that make each automatic condition false (D1
//     `subjects[]`). "2 proposals awaiting review" became "P-252 and P-253", and every named
//     subject carries a route to the surface where it is cleared (D4 `COND_ROUTE`, 27 entries,
//     asserted exhaustive by src/client/__tests__/lifecycleCondRoutes.test.ts).
//   · A condition met ONLY because its universe is empty reads `—  not yet applicable`, never as
//     held (D2 `applicable`). Without it the furthest gate outscored the nearest one: G8 read
//     3 of 6 on both live engagements purely because nothing had ever been recorded against it.
//   · The cross-gate consequence line states a shared SUBJECT, never a promised outcome (D3).
//     Accepting P-252 maps REQ-039; it does not confirm it. Copy of the form "clearing X
//     discharges N conditions" is a defect.
//   · A manual condition is ONE control: the tick and the note travel in one write, so the
//     operator never pays two round-trips and never sees the two disagree (D5).
//   · Deleted: the gate chip row, the nine-gate summary table and the audit table — three
//     re-renderings of numbers stated better elsewhere (21 of URSC's last 25 audit rows were
//     `portal.preview_token`). Inspection of every gate survives in the ahead/behind ledger.
//   · The engagement-record modal is demoted to a masthead link. It is NOT deleted: it is the
//     only place sponsor, kickoff date and estimate can be set, and each clears a gate condition.
//
// Kept: the optimistic overlay on manual ticks, the gate-panel cache, the force-override modal's
// deliberate weight, the ahead/behind read-only inspection.
import {
  Fragment,
  useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode,
} from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState from '../components/EmptyState.js';
import { TerminologyPanel } from '../components/TerminologyPanel.js';
import { usePoll, POLL_MS } from '../hooks/usePoll.js';
import { useOpenRoute, useRouteParams, useDeepLink, ROUTES, type Dest } from '../routes.js';
import * as crm from '../api/crm.js';
import { isLanguage, LANGUAGES, LANGUAGE_LABEL } from '../../shared/language.js';
import type { Gate, Phase, RailStep } from '../api/crm.js';
import * as life from '../api/lifecycle.js';
import type { GateSubject, LifeCondition, LifeGate, LifeGateRow, LifeState, LifeTransition } from '../api/lifecycle.js';
import {
  AUTO_CONDITION_LABELS, MANUAL_CONDITION_LABELS, routeFor, type RouteCtx,
} from './lifecycle/condRoutes.js';
import { useLocale, useT } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './EngagementPage.de.js';
import './EngagementPage.fr.js';
import './lifecycle/condRoutes.de.js';
import './lifecycle/condRoutes.fr.js';

/** `t()` for a sentence with markup inside: each `{name}` placeholder is filled with a React node. */
function tx(t: (key: string) => string, key: string, nodes: Record<string, ReactNode>): ReactNode {
  return t(key).split(/(\{\w+\})/g).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m ? <Fragment key={i}>{nodes[m[1]]}</Fragment> : part;
  });
}

/** The 'close' PHASE label collides with the fleet-wide verb key 'Close' ('Schliessen') — route it
 *  through its own key 'Close (phase)' ('Abschluss'). English keeps the plain word: a missing
 *  dictionary returns the key itself, so we fall back to 'Close' by hand. */
function tPhase(t: (key: string) => string, label: string): string {
  if (label !== 'Close') return t(label);
  const s = t('Close (phase)');
  return s === 'Close (phase)' ? 'Close' : s;
}

// ---------------------------------------------------------------------------
// Shared lifecycle presentation pieces (DashboardPage imports these)
// ---------------------------------------------------------------------------

const RAIL: RailStep[] = crm.PHASE_ORDER.map((p) => ({
  phase: p,
  label: crm.PHASE_LABELS[p],
  gate: crm.PHASE_GATES[p],
  gate_title: '',
  stage: crm.PHASE_STAGE[p],
}));

/**
 * The 9-step approach vector. LAW 2: position is carried by WEIGHT, never by hue —
 * the current step is full ink over a well, passed steps are muted ink, future steps are
 * a hairline. (This rail used to paint one of seven stage colours per step.)
 */
export function PhaseRail({
  current,
  compact = false,
  onPick,
  rail = RAIL,
}: {
  current: Phase;
  compact?: boolean;
  onPick?: (p: Phase) => void;
  rail?: RailStep[];
}) {
  const t = useT();
  const idx = crm.PHASE_ORDER.indexOf(current);
  return (
    <div className="flex items-end gap-4 w-full" role="list" aria-label={t('Phase rail')}>
      {rail.map((step, i) => {
        const passed = i < idx;
        const active = i === idx;
        const style: CSSProperties = {
          flex: 1,
          minWidth: 0,
          borderTop: `2px solid ${active ? 'var(--ink-1)' : passed ? 'var(--ink-4)' : 'var(--line)'}`,
          background: active ? 'var(--well)' : 'transparent',
          padding: compact ? '4px 2px 2px' : '6px 4px 4px',
          cursor: onPick ? 'pointer' : 'default',
          opacity: passed || active ? 1 : 0.55,
        };
        return (
          <div
            key={step.phase}
            role="listitem"
            title={`${tPhase(t, step.label)} · ${step.gate}`}
            style={style}
            onClick={onPick ? () => onPick(step.phase) : undefined}
          >
            {!compact && (
              <>
                <div
                  className="src-ref"
                  style={{ fontSize: 'var(--t-10)', letterSpacing: '0.08em', color: active ? 'var(--ink-1)' : passed ? 'var(--ink-3)' : 'var(--ink-4)' }}
                >
                  {step.gate}
                </div>
                <div
                  className="truncate"
                  style={{ fontSize: 'var(--t-11)', fontWeight: active ? 650 : 400, color: active ? 'var(--ink-1)' : 'var(--ink-3)' }}
                >
                  {tPhase(t, step.label)}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Gate lamp: ok when every condition is satisfied, warn while anything is outstanding. */
export function GateLampBadge({ gate, metCount, total, allMet }: { gate: string; metCount: number; total: number; allMet: boolean }) {
  const t = useT();
  return (
    <span className={`badge ${allMet ? 'badge-ok' : 'badge-warn'}`}>
      <span className="src-ref">{gate}</span>&nbsp;
      {allMet ? t('clear') : <span className="voice-figure">{`${metCount}/${total}`}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Derivations — pure, and every one a function of what the engine sent
// ---------------------------------------------------------------------------

type CondState = 'held' | 'open' | 'na';

/** THREE states, not two. A condition met only because its universe is empty is not held. */
const classify = (c: LifeCondition): CondState =>
  !c.met ? 'open' : c.applicable === false ? 'na' : 'held';

const MARK: Record<CondState, { glyph: string; cls: string; title: string }> = {
  held: { glyph: '▪', cls: 'lc-mark--held', title: 'held — ticked by the operator' },
  open: { glyph: '◇', cls: 'lc-mark--unheld', title: 'outstanding' },
  na: { glyph: '—', cls: 'lc-mark--na', title: 'not yet applicable' },
};

/** A computed condition carries no claim-mark hue when it is satisfied (ATTEST LAW 6). */
function markFor(c: LifeCondition, readOnly: boolean) {
  const st = classify(c);
  let m = MARK[st];
  if (c.kind === 'auto' && st === 'held') m = { glyph: '▪', cls: 'lc-mark--auto', title: 'read from the record' };
  // Nothing on a gate the engagement has not reached is actionable now, so nothing there takes
  // the attend hue: LAW 2's colour budget is spent on the current gate alone.
  return readOnly ? { ...m, cls: 'lc-mark--quiet' } : m;
}

/** Every ref named as a subject on any unmet condition, anywhere across the nine gates. */
function buildSubjectIndex(gates: LifeGateRow[]): Set<string> {
  const out = new Set<string>();
  for (const g of gates) for (const u of g.unmet) for (const s of u.subjects) out.add(s.ref);
  return out;
}

/**
 * D3, one hop. A subject's `covers` extends its reach — but only to refs that are THEMSELVES
 * subjects of something unmet. P-253 carries sixteen `satisfies_refs`; fifteen of them are
 * already confirmed and mapped, and naming those would be noise, not consequence.
 */
const rootsOf = (s: GateSubject, index: Set<string>): string[] => {
  const covered = (s.covers ?? []).filter((r) => index.has(r));
  return covered.length ? covered : [s.ref];
};

const reachOf = (s: GateSubject): string[] => [s.ref, ...(s.covers ?? [])];

interface Consequence { gate: Gate; label: string; shared: string[] }

/**
 * The conditions elsewhere that stand over the SAME rows. Stated as a shared subject, never as a
 * promised outcome — the page knows the rows are the same rows; it does not know that one action
 * satisfies every predicate over them.
 */
function consequencesOf(cond: LifeCondition, gate: Gate, gates: LifeGateRow[]): Consequence[] {
  if (!cond.subjects?.length) return [];
  const mine = new Set(cond.subjects.flatMap(reachOf));
  const out: Consequence[] = [];
  for (const g of gates) {
    for (const u of g.unmet) {
      if (g.gate === gate && u.label === cond.label) continue;
      const shared = u.subjects.filter((s) => reachOf(s).some((r) => mine.has(r))).map((s) => s.ref);
      if (shared.length) out.push({ gate: g.gate, label: u.label, shared });
    }
  }
  return out;
}

interface DebtRow { gate: Gate; still: string[]; total: number }

/** §F1 · what a forced crossing left behind that is STILL unmet today. Nothing new is stored. */
function forcedDebt(transitions: LifeTransition[], gates: LifeGateRow[]) {
  const liveUnmet = new Map<string, Set<string>>();
  for (const g of gates) liveUnmet.set(g.gate, new Set(g.unmet.map((u) => u.label)));

  const perTransition = new Map<number, DebtRow[]>();
  const perGate = new Map<string, number>();

  for (const t of transitions) {
    if (!t.forced) continue;
    const skipped = t.gate_snapshot?.gates_skipped ?? [];
    const rows: DebtRow[] = [];
    for (const s of skipped) {
      const live = liveUnmet.get(s.gate);
      const still = live ? s.unmet.map((u) => u.label).filter((l) => live.has(l)) : [];
      rows.push({ gate: s.gate, still, total: s.unmet.length });
      if (still.length) perGate.set(s.gate, (perGate.get(s.gate) ?? 0) + still.length);
    }
    if (rows.length) perTransition.set(t.id, rows);
  }
  return { perTransition, perGate };
}

// ---------------------------------------------------------------------------
// Small presentational parts (module scope — a component redeclared per render
// remounts its subtree, and a remounted <textarea> loses the caret mid-word)
// ---------------------------------------------------------------------------

const Ref = ({ children }: { children: ReactNode }) => <span className="src-ref">{children}</span>;
const Fig = ({ children }: { children: ReactNode }) => <span className="voice-figure">{children}</span>;

/** Join rendered fragments with ', ' and a final ' and ' — one sentence, not a list of pills. */
function joinNodes(items: ReactNode[], last = ' and ', sep = ', '): ReactNode[] {
  return items.map((n, i) => (
    <span key={i}>{i > 0 && (i === items.length - 1 ? last : sep)}{n}</span>
  ));
}

function Subjects({ c }: { c: LifeCondition }) {
  const t = useT();
  if (!c.subjects?.length) return null;
  return (
    <div className="lc-subjects">
      {c.subjects.map((s) => (
        <span key={`${s.kind}-${s.id}`} className="lc-subj" title={s.label ? `${s.ref} — ${s.label}` : s.ref}>
          {s.ref}
        </span>
      ))}
      {!!c.subject_overflow && <span className="lc-subj-more">{t('and {n} more', { n: c.subject_overflow })}</span>}
    </div>
  );
}

interface CondRowProps {
  c: LifeCondition;
  gate: Gate;
  /** A gate the engagement has not reached (or has already crossed) is inspected, not worked. */
  readOnly: boolean;
  gates: LifeGateRow[];
  routeCtx: RouteCtx | null;
  noteDraft: Record<string, string>;
  savedFlash: Record<string, boolean>;
  onDraft: (key: string, value: string | undefined) => void;
  onSave: (gate: Gate, label: string, change: { checked?: boolean; note?: string | null }) => void;
  onRoute: (dest: Dest) => void;
  onOpenRecord: () => void;
  onRemove: (item: { id: number; label: string }) => void;
}

/** The G0 auto condition answered by writing, and the checklist item its note is filed on. */
const QUALIFICATION_COND = 'Qualification note recorded for this engagement';
const QUALIFICATION_ITEM = 'Qualification note';

function CondRow({
  c, gate, readOnly, gates, routeCtx, noteDraft, savedFlash, onDraft, onSave, onRoute, onOpenRecord, onRemove,
}: CondRowProps) {
  const { t, tp } = useLocale();
  const st = classify(c);
  const m = markFor(c, readOnly);
  const key = `${gate}:${c.label}`;
  const auto = c.kind === 'auto';
  const route = auto && st === 'open' && !readOnly ? routeFor(gate, c.label) : undefined;
  const cons = auto && st === 'open' && !readOnly ? consequencesOf(c, gate, gates) : [];
  const noteValue = noteDraft[key] ?? c.note ?? '';
  const named = (c.subjects?.length ?? 0) + (c.subject_overflow ?? 0);
  const sharedRefs = [...new Set(cons.flatMap((x) => x.shared))];
  const and = ` ${t('and')} `;

  const tick = () => {
    const draft = noteDraft[key];
    onSave(gate, c.label, {
      checked: !c.met,
      ...(draft !== undefined && draft.trim() !== (c.note ?? '') ? { note: draft.trim() || null } : {}),
    });
  };

  return (
    <div className={`lc-cond ${st === 'open' ? 'lc-cond--open' : 'lc-cond--met'}`}>
      {auto || readOnly ? (
        <div className={`lc-mark ${m.cls}`} title={t(m.title)}>{m.glyph}</div>
      ) : (
        <button
          type="button"
          className={`lc-mark ${m.cls}`}
          title={c.met ? t('Untick this condition') : t('Tick this condition')}
          aria-pressed={c.met}
          onClick={tick}
        >
          {m.glyph}
        </button>
      )}

      <div style={{ minWidth: 0 }}>
        <div className="lc-clabel">{t(c.label)}</div>

        {auto ? (
          <>
            <div className={`lc-cdetail ${st === 'open' ? 'lc-cdetail--open' : ''}`}>
              {c.detail}
              {st === 'na' && <span className="n-none"> — {t('not yet applicable')}: {c.vacuous_reason}</span>}
            </div>
            {st === 'open' && <Subjects c={c} />}
            {/* The one auto condition an operator satisfies by WRITING: the qualification note
                lives as the note on a G0 checklist item — put the field right here, so nobody
                has to guess which item's note the engine reads. */}
            {c.label === QUALIFICATION_COND && st === 'open' && !readOnly && (
              <div className="lc-noteline" style={{ marginTop: 6 }}>
                <textarea
                  className="lc-note"
                  rows={2}
                  placeholder={t('Write the qualification note here (at least 20 characters) — why this engagement is worth pursuing')}
                  value={noteDraft[`${gate}:${QUALIFICATION_ITEM}`] ?? ''}
                  onChange={(e) => {
                    onDraft(`${gate}:${QUALIFICATION_ITEM}`, e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight + 2}px`;
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v === '') { onDraft(`${gate}:${QUALIFICATION_ITEM}`, undefined); return; }
                    onSave(gate, QUALIFICATION_ITEM, { note: v });
                  }}
                />
                <span className="lc-saved" style={{ opacity: savedFlash[`${gate}:${QUALIFICATION_ITEM}`] ? 1 : 0 }} aria-live="polite">{t('saved')}</span>
              </div>
            )}
            <div className="lc-routes">
              {route?.dest && routeCtx && (
                <button type="button" className="lc-route" onClick={() => onRoute(route.dest!(routeCtx))}>
                  {t(route.label)}{named > 0 && <> <span className="n">{named}</span></>} →
                </button>
              )}
              {route && !route.dest && route.self === 'record' && (
                <button type="button" className="lc-route" onClick={onOpenRecord}>{t(route.label)} →</button>
              )}
              {route && !route.dest && route.self === 'gate' && (
                <span className="lc-kind">{t(route.label)}</span>
              )}
              <span className="lc-kind">
                {readOnly ? t('automatic') : t('automatic · the engine reads this, you cannot tick it')}
              </span>
            </div>
            {cons.length > 0 && (
              <div className="lc-conseq">
                {tx(t, 'The same subjects also sit under {list}{more}. {n} conditions, {refs}{moreRefs}, one piece of work.', {
                  list: joinNodes(cons.slice(0, 3).map((x) => (<><Ref>{x.gate}</Ref> · {t(x.label)}</>)), and),
                  more: cons.length > 3 ? <> {tp(cons.length - 3, 'and {n} other condition', 'and {n} other conditions')}</> : null,
                  n: <Fig>{cons.length + 1}</Fig>,
                  refs: joinNodes(sharedRefs.slice(0, 6).map((r) => <Ref>{r}</Ref>), and),
                  moreRefs: sharedRefs.length > 6 ? <> {t('and {n} more', { n: sharedRefs.length - 6 })}</> : null,
                })}
              </div>
            )}
          </>
        ) : readOnly ? (
          <div className="lc-cdetail">{c.met ? t('manual · ticked') : t('manual · not yet ticked')}</div>
        ) : (
          <>
            <div className="lc-noteline">
              <textarea
                className="lc-note"
                rows={1}
                placeholder={t('why is this true? — recorded on the gate, quoted back in the record')}
                value={noteValue}
                onChange={(e) => {
                  onDraft(key, e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight + 2}px`;
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if ((c.note ?? '') === v) { onDraft(key, undefined); return; }
                  // Note only — the tick is deliberately not sent, so the audit action resolves
                  // to gate.item.note and never to an uncheck.
                  onSave(gate, c.label, { note: v || null });
                }}
              />
              <span className="lc-saved" style={{ opacity: savedFlash[key] ? 1 : 0 }} aria-live="polite">{t('saved')}</span>
            </div>
            <div className="lc-routes">
              <span className="lc-kind">{t('manual · you are the source')}</span>
              {c.custom && c.item_id != null && (
                <button
                  type="button"
                  className="lc-route"
                  onClick={() => onRemove({ id: c.item_id!, label: c.label })}
                >
                  {t('Remove this custom condition')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface GatePanelProps {
  row: LifeGateRow;
  open: boolean;
  detail: LifeGate | null | undefined;
  error?: string;
  owed: number;
  /** How many gates a jump from the current gate to this one would cross. 0 for a crossed gate. */
  crossings: number;
  currentGate: Gate;
  endpoint: string;
  onToggle: (g: Gate) => void;
  cond: (c: LifeCondition, gate: Gate) => ReactNode;
}

function GatePanel({
  row, open, detail, error, owed, crossings, currentGate, endpoint, onToggle, cond,
}: GatePanelProps) {
  const t = useT();
  return (
    <>
      <button type="button" className="lc-arow" aria-expanded={open} onClick={() => onToggle(row.gate)}>
        <div className="g">{row.gate}</div>
        <div className="t">
          {row.title}
          <small>{tPhase(t, crm.PHASE_LABELS[row.phase])}</small>
        </div>
        <div className="st">
          {tx(t, '{open} outstanding · {held} held', { open: <Fig>{row.openCount}</Fig>, held: <Fig>{row.heldCount}</Fig> })}
          {row.naCount > 0 && <> · <span className="na">{tx(t, '{n} n/a', { n: <Fig>{row.naCount}</Fig> })}</span></>}
          {owed > 0 && <> · <span style={{ color: 'var(--attend)' }}>{t('{n} skipped', { n: owed })}</span></>}
        </div>
        <div className="chev">{open ? '▾' : '▸'}</div>
      </button>
      {open && (
        <div className="lc-apanel">
          {detail === undefined && !error && <div className="lc-cdetail">{t('Reading {gate}…', { gate: row.gate })}</div>}
          {error && (
            <div className="lc-cdetail lc-cdetail--open">
              {tx(t, '{subject} — “{error}”. Nothing was written.', {
                subject: <span className="n-unknown" title={error}>{t('{gate} could not be evaluated', { gate: row.gate })}</span>,
                error,
              })}
            </div>
          )}
          {detail?.conditions.map((c) => cond(c, row.gate))}
          <div className="lc-endpoint">{endpoint}</div>
          {crossings > 0 && (
            <div className="lc-cdetail" style={{ marginTop: 6 }}>
              {tx(t, crossings === 1
                ? 'Crossing {from} → {to} means clearing {n} gate, in order.'
                : 'Crossing {from} → {to} means clearing {n} gates, in order.', {
                from: <Ref>{currentGate}</Ref>,
                to: <Ref>{row.gate}</Ref>,
                n: <Fig>{crossings}</Fig>,
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface EngagementForm {
  name: string;
  commercial_model: string;
  currency: string;
  language: string;
  review_window_days: string;
  kickoff_date: string;
  parent_engagement_id: string;
  sponsor_contact_id: string;
  estimate_value: string;
}

const EMPTY_EDIT: EngagementForm = {
  name: '', commercial_model: 'fixed', currency: 'CHF', language: 'de',
  review_window_days: '10', kickoff_date: '', parent_engagement_id: '',
  sponsor_contact_id: '', estimate_value: '',
};

export default function EngagementPage() {
  const nav = useEngagement();
  const { current, engagements, reload: reloadEngagements } = nav;
  const openRoute = useOpenRoute(nav);
  const toast = useToast();
  const { t, tp, fmtDate, fmtDateTime, fmtNum } = useLocale();
  const engagementId = current?.id ?? null;
  const and = ` ${t('and')} `;

  // Lifecycle takes only `anchor`, which useDeepLink handles by default.
  useDeepLink(useRouteParams(), {});

  const load = useCallback(
    async (): Promise<LifeState | null> => (engagementId ? life.getLifecycleState(engagementId) : null),
    [engagementId],
  );
  const { data: state, loading, error, notice, refresh } = usePoll(load, POLL_MS);

  const [busy, setBusy] = useState(false);
  const [showHeld, setShowHeld] = useState(false);
  const [openGate, setOpenGate] = useState<Gate | null>(null);
  const [gateCache, setGateCache] = useState<Record<string, LifeGate | null>>({});
  const [gateError, setGateError] = useState<Record<string, string>>({});
  const [showBehind, setShowBehind] = useState(false);

  const [forceTarget, setForceTarget] = useState<Phase | null>(null);
  const [forceReason, setForceReason] = useState('');
  const [forceAck, setForceAck] = useState(false);
  const [backOpen, setBackOpen] = useState(false);

  // `pending` is the optimistic overlay: a click paints instantly and only yields to the server
  // once the PUT and the reload have landed. On failure it rolls back.
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Record<string, { checked?: boolean; note?: string }>>({});
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});
  const [newItemLabel, setNewItemLabel] = useState('');
  const [removeItem, setRemoveItem] = useState<{ id: number; label: string } | null>(null);

  // The engagement record: demoted from the page's top-right button to a masthead link, but not
  // removed — sponsor, kickoff date and estimate each clear a gate condition and live only here.
  const [editOpen, setEditOpen] = useState(false);
  const [contacts, setContacts] = useState<crm.Contact[]>([]);
  const [editForm, setEditForm] = useState<EngagementForm>(EMPTY_EDIT);
  const [archiveConfirm, setArchiveConfirm] = useState(false);

  // The no-engagement state routes to every live engagement WITH its gate state in the label.
  const [lamps, setLamps] = useState<crm.GateLamp[] | null>(null);
  useEffect(() => {
    if (engagementId) return;
    let alive = true;
    crm.getGateLamps().then((l) => { if (alive) setLamps(l); }).catch(() => { if (alive) setLamps([]); });
    return () => { alive = false; };
  }, [engagementId]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // A different engagement is a different subject: nothing expanded and nothing half-typed carries.
  useEffect(() => {
    setOpenGate(null); setGateCache({}); setGateError({}); setShowHeld(false);
    setShowBehind(false); setPending({}); setNoteDraft({});
  }, [engagementId]);

  const eng = state?.engagement ?? null;
  const gates = useMemo<LifeGateRow[]>(() => state?.gates ?? [], [state]);
  const rail = state?.rail ?? RAIL;
  const engineDown = !!state && !state.gate;
  const gateProblem = state?.problems.find((p) => p.source === 'gates') ?? null;

  /** The current gate's conditions with the optimistic overlay applied. */
  const conditions = useMemo<LifeCondition[]>(() => {
    const g = state?.gate;
    if (!g) return [];
    return g.conditions.map((c) => {
      const o = pending[`${g.gate}:${c.label}`];
      if (!o) return c;
      return {
        ...c,
        ...(o.checked === undefined ? {} : { met: o.checked }),
        ...(o.note === undefined ? {} : { note: o.note }),
      };
    });
  }, [state, pending]);

  const openConds = conditions.filter((c) => classify(c) === 'open');
  const restConds = conditions.filter((c) => classify(c) !== 'open');
  const naCount = conditions.filter((c) => classify(c) === 'na').length;
  const heldCount = conditions.length - openConds.length - naCount;
  const clear = !!state?.gate && openConds.length === 0;

  const subjectIndex = useMemo(() => buildSubjectIndex(gates), [gates]);
  const debt = useMemo(() => forcedDebt(state?.transitions ?? [], gates), [state, gates]);

  const phaseIdx = eng ? crm.PHASE_ORDER.indexOf(eng.current_phase) : -1;
  const nextPhase: Phase | null =
    phaseIdx >= 0 && phaseIdx < crm.PHASE_ORDER.length - 1 ? crm.PHASE_ORDER[phaseIdx + 1] : null;
  const backPhases: Phase[] = phaseIdx > 0 ? crm.PHASE_ORDER.slice(0, phaseIdx) : [];
  const nextGateRow = nextPhase ? gates.find((g) => g.gate === crm.PHASE_GATES[nextPhase]) ?? null : null;
  const routeCtx: RouteCtx | null = eng ? { engagementId: eng.id, clientId: eng.client_id } : null;

  // ---- actions --------------------------------------------------------------

  async function doTransition(to: Phase, force = false, reason?: string) {
    if (!engagementId) return;
    setBusy(true);
    try {
      const res = await crm.transition(engagementId, { to_phase: to, force, override_reason: reason });
      const move = {
        from: tPhase(t, crm.PHASE_LABELS[res.transition.from_phase] ?? res.transition.from_phase),
        to: tPhase(t, crm.PHASE_LABELS[res.transition.to_phase] ?? res.transition.to_phase),
      };
      toast(force ? 'warn' : 'ok', force ? t('{from} → {to} (forced)', move) : t('{from} → {to}', move));
      setForceTarget(null); setForceReason(''); setForceAck(false); setBackOpen(false);
      setOpenGate(null); setGateCache({}); setShowHeld(false);
      await Promise.all([refresh(), reloadEngagements()]);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  /**
   * D5 · ONE request for whatever changed — the tick, the note, or both. The mark is the click
   * target and the note is a field beside it, so ticking sends whatever the note currently holds
   * and the operator never sees the two disagree.
   */
  const saveManual = useCallback(
    async (gate: Gate, label: string, change: { checked?: boolean; note?: string | null }) => {
      if (!engagementId) return;
      const key = `${gate}:${label}`;
      let rollback: { checked?: boolean; note?: string } | undefined;
      setPending((p) => {
        rollback = p[key];
        return {
          ...p,
          [key]: {
            ...p[key],
            ...(change.checked === undefined ? {} : { checked: change.checked }),
            ...(change.note === undefined ? {} : { note: change.note ?? '' }),
          },
        };
      });
      try {
        await crm.setGateItem(engagementId, gate, { label, ...change });
        await refresh();
        if (!mounted.current) return;
        setPending((p) => { const next = { ...p }; delete next[key]; return next; });
        if (change.note !== undefined) {
          setNoteDraft((d) => { const next = { ...d }; delete next[key]; return next; });
          setSavedFlash((f) => ({ ...f, [key]: true }));
          setTimeout(() => { if (mounted.current) setSavedFlash((f) => ({ ...f, [key]: false })); }, 1800);
        }
      } catch (err) {
        setPending((p) => {
          const next = { ...p };
          if (rollback) next[key] = rollback; else delete next[key];
          return next;
        });
        toast('err', (err as Error).message);
      }
    },
    [engagementId, refresh, toast],
  );

  const setDraft = useCallback((key: string, value: string | undefined) => {
    setNoteDraft((d) => {
      const next = { ...d };
      if (value === undefined) delete next[key]; else next[key] = value;
      return next;
    });
  }, []);

  async function addManual(gate: Gate) {
    if (!engagementId || !newItemLabel.trim()) return;
    try {
      await crm.addGateItem(engagementId, gate, { label: newItemLabel.trim() });
      setNewItemLabel('');
      toast('ok', t('Condition added'));
      await refresh();
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function confirmRemoveItem() {
    if (!engagementId || !state?.gate || !removeItem) return;
    try {
      await crm.deleteGateItem(engagementId, state.gate.gate, removeItem.id);
      toast('ok', t('Condition removed'));
      setRemoveItem(null);
      await refresh();
    } catch (err) {
      toast('err', (err as Error).message);
      setRemoveItem(null);
    }
  }

  const toggleGatePanel = useCallback(async (g: Gate) => {
    if (!engagementId) return;
    let alreadyOpen = false;
    setOpenGate((cur) => { alreadyOpen = cur === g; return alreadyOpen ? null : g; });
    if (alreadyOpen) return;
    if (gateCache[g] !== undefined) return;
    try {
      const reply = await life.getLifecycleGate(engagementId, g);
      if (!mounted.current) return;
      setGateCache((c) => ({ ...c, [g]: reply.gate }));
      const p = reply.problems.find((x) => x.source === 'gates');
      if (p) setGateError((e) => ({ ...e, [g]: p.message }));
    } catch (err) {
      if (mounted.current) setGateError((e) => ({ ...e, [g]: (err as Error).message }));
    }
  }, [engagementId, gateCache]);

  function openEdit() {
    if (!eng) return;
    setEditForm({
      name: eng.name,
      commercial_model: eng.commercial_model,
      currency: eng.currency,
      language: eng.language ?? 'de',
      review_window_days: String(eng.review_window_days),
      kickoff_date: eng.kickoff_date ? String(eng.kickoff_date).slice(0, 10) : '',
      parent_engagement_id: eng.parent_engagement_id ? String(eng.parent_engagement_id) : '',
      sponsor_contact_id: eng.sponsor_contact_id ? String(eng.sponsor_contact_id) : '',
      estimate_value: eng.estimate_value != null ? String(eng.estimate_value) : '',
    });
    crm.getClientContacts(eng.client_id).then(setContacts).catch(() => setContacts([]));
    setEditOpen(true);
  }

  async function saveEngagement() {
    if (!engagementId) return;
    if (!editForm.name.trim()) { toast('warn', t('Name cannot be empty')); return; }
    const windowDays = Number(editForm.review_window_days);
    if (!Number.isInteger(windowDays) || windowDays < 0 || windowDays > 365) {
      toast('warn', t('Review window must be a whole number of days between 0 and 365'));
      return;
    }
    setBusy(true);
    try {
      await crm.updateEngagement(engagementId, {
        name: editForm.name.trim(),
        commercial_model: editForm.commercial_model,
        currency: editForm.currency.trim().toUpperCase() || 'CHF',
        language: editForm.language,
        review_window_days: windowDays,
        kickoff_date: editForm.kickoff_date || null,
        parent_engagement_id: editForm.parent_engagement_id ? Number(editForm.parent_engagement_id) : null,
        sponsor_contact_id: editForm.sponsor_contact_id ? Number(editForm.sponsor_contact_id) : null,
        estimate_value: editForm.estimate_value.trim() ? Number(editForm.estimate_value) : null,
      });
      toast('ok', t('Engagement updated'));
      setEditOpen(false);
      await Promise.all([refresh(), reloadEngagements()]);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function setArchived(archived: boolean) {
    if (!engagementId) return;
    setBusy(true);
    try {
      await crm.updateEngagement(engagementId, { archived });
      toast(archived ? 'warn' : 'ok', archived ? t('Engagement archived') : t('Engagement restored'));
      setArchiveConfirm(false);
      setEditOpen(false);
      await Promise.all([refresh(), reloadEngagements()]);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  /** One binding site for every condition row on the page. */
  const renderCond = useCallback(
    (c: LifeCondition, gate: Gate, readOnly = false) => (
      <CondRow
        key={`${gate}:${c.label}`}
        c={c}
        gate={gate}
        readOnly={readOnly}
        gates={gates}
        routeCtx={routeCtx}
        noteDraft={noteDraft}
        savedFlash={savedFlash}
        onDraft={setDraft}
        onSave={saveManual}
        onRoute={(dest) => void openRoute(dest)}
        onOpenRecord={openEdit}
        onRemove={setRemoveItem}
      />
    ),
    // openEdit closes over `eng`, which changes with `state`; the deps below cover it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gates, routeCtx, noteDraft, savedFlash, setDraft, saveManual, openRoute, eng],
  );

  // ── no engagement ──
  if (!current) {
    return (
      <div className="page lc">
        <header className="mast">
          <div className="mast-top"><div className="mast-main">
            <h1>{t('Lifecycle')}</h1>
            <div className="mast-sub">{t('no engagement selected')}</div>
          </div></div>
        </header>
        <div className="lc-state">
          <h3>{t('Lifecycle is a per-engagement surface')}</h3>
          <p>
            {tx(t, 'A phase, a gate and every checklist tick hang off one engagement — {a}, {b}. There is nothing to show until one is chosen.', {
              a: <Ref>engagements.current_phase</Ref>,
              b: <Ref>gate_checklist_items.engagement_id</Ref>,
            })}
          </p>
          <div className="lc-routes">
            {engagements.map((e) => {
              const lamp = lamps?.find((l) => l.engagement_id === e.id) ?? null;
              const phase = tPhase(t, crm.PHASE_LABELS[e.current_phase as Phase] ?? e.current_phase);
              return (
                <button
                  key={e.id}
                  type="button"
                  className="lc-route"
                  onClick={() => void openRoute(ROUTES.lifecycle({ engagement: e.id }))}
                >
                  {e.code} · {phase}
                  {lamp && (
                    <> · {lamp.gate} · {lamp.allMet
                      ? t('clear')
                      : t('{n} of {total} outstanding', { n: lamp.unmet.length, total: lamp.total })}</>
                  )}
                  {' →'}
                </button>
              );
            })}
            {!engagements.length && <span className="lc-hint">{t('No engagement exists yet.')}</span>}
          </div>
        </div>
        <div className="lc-foot">
          {tx(t, 'The gate state in each label comes from {endpoint}.', { endpoint: <Ref>GET /api/engagements/gates/summary</Ref> })}
        </div>
      </div>
    );
  }

  // ── first load: the masthead is already true, only the gate is unknown ──
  if (loading && !state) {
    return (
      <div className="page lc">
        <header className="mast">
          <div className="mast-top"><div className="mast-main">
            <h1><Ref>{current.code}</Ref> <span style={{ color: 'var(--ink-4)' }}>·</span> {current.name}</h1>
            <div className="mast-sub">{current.client_name ?? ''}</div>
          </div></div>
        </header>
        <div className="lc-skel">{t('Reading the gate…')}</div>
      </div>
    );
  }

  // ── the read itself failed and nothing has ever loaded ──
  if (!state || !eng) {
    return (
      <div className="page lc">
        <header className="mast">
          <div className="mast-top"><div className="mast-main">
            <h1><Ref>{current.code}</Ref> <span style={{ color: 'var(--ink-4)' }}>·</span> {current.name}</h1>
            <div className="mast-sub">{t('the console could not be read')}</div>
          </div></div>
        </header>
        <EmptyState
          kind="error"
          title={t('The lifecycle console could not be loaded')}
          detail={tx(t, 'The endpoint {endpoint} answered: “{error}”', {
            endpoint: <code>GET /api/lifecycle/{engagementId}/state</code>,
            error: error ?? t('no answer'),
          })}
          onRetry={() => void refresh()}
        />
      </div>
    );
  }

  const here = tPhase(t, crm.PHASE_LABELS[eng.current_phase]);
  const forcedCount = state.transitions.filter((tr) => tr.forced).length;
  const oldest = state.transitions.length ? state.transitions[state.transitions.length - 1] : null;

  // The verdict's subject collapse: four rows, two requirements.
  const autoOpen = openConds.filter((c) => c.kind === 'auto');
  const manualOpen = openConds.filter((c) => c.kind === 'manual');
  const rawRefs = [...new Set(autoOpen.flatMap((c) => (c.subjects ?? []).map((s) => s.ref)))];
  const rootRefs = [...new Set(autoOpen.flatMap((c) => (c.subjects ?? []).flatMap((s) => rootsOf(s, subjectIndex))))];

  const aheadGates = gates.filter((g) => crm.PHASE_ORDER.indexOf(g.phase) > phaseIdx);
  const behindGates = gates.filter((g) => crm.PHASE_ORDER.indexOf(g.phase) < phaseIdx);
  const aheadNa = aheadGates.reduce((n, g) => n + g.naCount, 0);
  const behindOwed = behindGates.reduce((n, g) => n + (debt.perGate.get(g.gate) ?? 0), 0);

  const panelFor = (row: LifeGateRow) => (
    <GatePanel
      key={row.gate}
      row={row}
      open={openGate === row.gate}
      detail={gateCache[row.gate]}
      error={gateError[row.gate]}
      owed={debt.perGate.get(row.gate) ?? 0}
      crossings={Math.max(0, crm.PHASE_ORDER.indexOf(row.phase) - phaseIdx)}
      currentGate={eng.gate}
      endpoint={life.gateSource(eng.id, row.gate)}
      onToggle={(g) => void toggleGatePanel(g)}
      cond={(c, gate) => renderCond(c, gate, true)}
    />
  );

  return (
    <div className="page lc">
      {/* ── A · Masthead ── */}
      <header className="mast">
        <div className="mast-top">
        <div className="mast-main">
          <h1>
            <Ref>{eng.code}</Ref> <span style={{ color: 'var(--ink-4)' }}>·</span> {eng.name}
          </h1>
          <div className="mast-sub">
            <span>{eng.client_name}</span>
            <span className="sep">·</span>
            <span>{tx(t, 'sponsor {name}', { name: eng.sponsor_name ?? <span className="n-none">{t('not named')}</span> })}</span>
            {eng.parent_code && (<><span className="sep">·</span><span>{tx(t, 'child of {code}', { code: <Ref>{eng.parent_code}</Ref> })}</span></>)}
            <span className="sep">·</span>
            <span>{t(crm.COMMERCIAL_MODEL_LABELS[eng.commercial_model] ?? eng.commercial_model)}</span>
            <span className="sep">·</span>
            <span title={t('The correspondence language — everything produced inside this engagement is written in it.')}>{isLanguage(eng.language) ? LANGUAGE_LABEL[eng.language] : eng.language}</span>
            <span className="sep">·</span>
            <span>
              {tx(t, 'estimate {value}', {
                value: eng.estimate_value != null
                  ? <><Ref>{eng.currency}</Ref> <Fig>{fmtNum(eng.estimate_value)}</Fig></>
                  : <span className="n-none">{t('none')}</span>,
              })}
            </span>
            <span className="sep">·</span>
            <span>{tx(t, 'kickoff {date}', { date: eng.kickoff_date ? <Fig>{fmtDate(eng.kickoff_date)}</Fig> : <span className="n-none">{t('not set')}</span> })}</span>
            <span className="sep">·</span>
            <span>{tx(t, 'review window {n} d', { n: <Fig>{eng.review_window_days}</Fig> })}</span>
            {eng.archived_at && (<><span className="sep">·</span><span className="lc-badge">{t('archived')}</span></>)}
          </div>
        </div>
        <div className="lc-mast-side">
          <button type="button" className="lc-link" onClick={openEdit}>{t('Engagement record →')}</button>
          <div className="lc-hint" style={{ marginTop: 6 }}>{t('name · sponsor · estimate · archive')}</div>
        </div>
        </div>
      </header>

      {notice && <div className="lc-notice">{notice}</div>}

      {/* ── B · Verdict — the page's one loud element ── */}
      <section className={`lc-verdict ${engineDown ? 'lc-verdict--broken' : clear ? 'lc-verdict--clear' : ''}`}>
        <div className="lc-vhead">
          <span className="u-label">{t('Verdict')}</span>
          <span className="u-label" style={{ color: 'var(--ink-3)' }}>
            <Ref>{eng.gate}</Ref> · {engineDown ? t('not evaluated') : eng.gate_title}
          </span>
        </div>

        {engineDown ? (
          <>
            <p className="lc-vlead">{tx(t, 'The gate engine did not answer, so {verdict}.', { verdict: <b>{t('no verdict can be given')}</b> })}</p>
            <div className="lc-because">
              {tx(t, 'The phase is a column and is still true: {code} is in {phase}, step {step} of {count}. The conditions are queries and they failed — showing them as unmet would be a guess, and showing them as met would be a lie.', {
                code: <Ref>{eng.code}</Ref>,
                phase: <b>{here}</b>,
                step: <Fig>{eng.phase_step}</Fig>,
                count: <Fig>{eng.phase_count}</Fig>,
              })}
            </div>
          </>
        ) : clear ? (
          <p className="lc-vlead">
            {tx(t, '{code} can leave {phase}. All {n} conditions on {gate} are held.', {
              code: <Ref>{eng.code}</Ref>,
              phase: <b>{here}</b>,
              n: <Fig>{conditions.length}</Fig>,
              gate: <Ref>{eng.gate}</Ref>,
            })}
          </p>
        ) : (
          <>
            <p className="lc-vlead">
              {tx(t, '{code} cannot leave {phase}: {open} of {total} conditions on {gate} are outstanding.', {
                code: <Ref>{eng.code}</Ref>,
                phase: <b>{here}</b>,
                open: <Fig>{openConds.length}</Fig>,
                total: <Fig>{conditions.length}</Fig>,
                gate: <Ref>{eng.gate}</Ref>,
              })}
            </p>
            <div className="lc-because">
              {joinNodes([
                ...(manualOpen.length ? [tx(t, '{n} you can tick here', { n: <Fig>{manualOpen.length}</Fig> })] : []),
                ...(autoOpen.length ? [tx(t, '{n} the engine reads elsewhere', { n: <Fig>{autoOpen.length}</Fig> })] : []),
              ], ' · ', ' · ')}
              {rootRefs.length > 0 && rootRefs.length < rawRefs.length ? (
                tx(t, '. The {n} automatic subjects — {raw}{rawMore} — all come down to {roots}{rootsMore}.', {
                  n: <Fig>{rawRefs.length}</Fig>,
                  raw: joinNodes(rawRefs.slice(0, 8).map((r) => <Ref>{r}</Ref>), ', '),
                  rawMore: rawRefs.length > 8 ? <> {t('and {n} more', { n: rawRefs.length - 8 })}</> : null,
                  roots: joinNodes(rootRefs.slice(0, 8).map((r) => <Ref>{r}</Ref>), and),
                  rootsMore: rootRefs.length > 8 ? <> {t('and {n} more', { n: rootRefs.length - 8 })}</> : null,
                })
              ) : rawRefs.length > 0 ? (
                tx(t, '. The automatic ones come down to {raw}{rawMore}.', {
                  raw: joinNodes(rawRefs.slice(0, 8).map((r) => <Ref>{r}</Ref>), and),
                  rawMore: rawRefs.length > 8 ? <> {t('and {n} more', { n: rawRefs.length - 8 })}</> : null,
                })
              ) : null}
            </div>
          </>
        )}

        <div className="lc-acts">
          {nextPhase ? (
            <>
              <button
                type="button"
                className="btn btn-accent"
                disabled={busy || !clear}
                title={clear ? t('Move to {phase}', { phase: tPhase(t, crm.PHASE_LABELS[nextPhase]) }) : t('Gate {gate} is not clear', { gate: eng.gate })}
                onClick={() => void doTransition(nextPhase)}
              >
                {t('Advance to {phase}', { phase: tPhase(t, crm.PHASE_LABELS[nextPhase]) })}
              </button>
              {!clear && (
                <button
                  type="button"
                  className="lc-quiet lc-quiet--attend"
                  disabled={busy}
                  onClick={() => { setForceTarget(nextPhase); setForceReason(''); setForceAck(false); }}
                >
                  {t('Force past {gate}…', { gate: eng.gate })}
                </button>
              )}
              <span className="lc-hint">
                {tx(t, 'lands in {phase}, guarded by {gate}', {
                  phase: <b>{tPhase(t, crm.PHASE_LABELS[nextPhase])}</b>,
                  gate: <Ref>{crm.PHASE_GATES[nextPhase]}</Ref>,
                })}
                {nextGateRow
                  ? <> · {tx(t, '{n} outstanding there', { n: <Fig>{nextGateRow.openCount}</Fig> })}</>
                  : <> · <span className="n-unknown" title={t('the gate engine did not answer')}>{t('tally unknown')}</span></>}
              </span>
            </>
          ) : (
            <span className="lc-hint">{t('Final phase — no gate ahead.')}</span>
          )}
          <span className="lc-spring" />
          {engineDown && (
            <button type="button" className="btn" onClick={() => void refresh()}>{t('Re-evaluate the gate')}</button>
          )}
          {backPhases.length > 0 && (
            <button type="button" className="lc-quiet" disabled={busy} onClick={() => setBackOpen(true)}>
              {t('Step back…')}
            </button>
          )}
        </div>
      </section>

      {/* ── C · Approach vector ── */}
      <div className="lc-railsheet">
        <div className="lc-rail" role="list" aria-label={t('Approach vector')}>
          {rail.map((step, i) => {
            const cls = i < phaseIdx ? 'lc-rstep--passed' : i === phaseIdx ? 'lc-rstep--active' : '';
            const owed = debt.perGate.get(step.gate) ?? 0;
            return (
              <button
                key={step.phase}
                type="button"
                role="listitem"
                className={`lc-rstep ${cls} ${owed ? 'lc-rstep--debt' : ''}`}
                title={
                  `${tPhase(t, step.label)} · ${step.gate}${step.gate_title ? ` ${step.gate_title}` : ''}`
                  + (owed ? ` — ${tp(owed, '{n} condition skipped at a forced crossing, still unmet', '{n} conditions skipped at a forced crossing, still unmet')}` : '')
                  + (i === phaseIdx ? '' : ` — ${t('click to inspect')}`)
                }
                onClick={() => { if (i !== phaseIdx) void toggleGatePanel(step.gate); if (i < phaseIdx) setShowBehind(true); }}
              >
                <div className="g">{step.gate}</div>
                <div className="l">{tPhase(t, step.label)}</div>
              </button>
            );
          })}
        </div>
        <div className="lc-railcap">
          {tx(t, 'Step {step} of {count}', { step: <Fig>{eng.phase_step}</Fig>, count: <Fig>{eng.phase_count}</Fig> })}
          {' · '}
          {forcedCount
            ? <b>{tx(t, forcedCount === 1 ? '{n} forced crossing on the record' : '{n} forced crossings on the record', { n: <Fig>{forcedCount}</Fig> })}</b>
            : tx(t, '{n} forced crossings on the record', { n: <Fig>0</Fig> })}
          {' · '}
          {tx(t, state.transitions.length === 1 ? '{n} transition' : '{n} transitions', { n: <Fig>{state.transitions.length}</Fig> })}
          {oldest ? <> {tx(t, 'since {date}', { date: <Fig>{fmtDate(oldest.created_at)}</Fig> })}</> : ` ${t('recorded')}`}
        </div>
      </div>

      {/* ── D · The gate ── */}
      <section className="zone lc-sec" id="gate">
        <div className="zone-head">
          <h2 className="u-label"><Ref>{eng.gate}</Ref> · {engineDown ? t('not evaluated') : eng.gate_title}</h2>
          <span className="zone-note zone-note--end lc-tally">
            {engineDown ? (
              <span className="n-none">{t('— not evaluated')}</span>
            ) : (
              <>
                {tx(t, '{open} outstanding · {held} held', { open: <Fig>{openConds.length}</Fig>, held: <Fig>{heldCount}</Fig> })}
                {naCount > 0 && <> · <span className="na">{tx(t, '{n} not yet applicable', { n: <Fig>{naCount}</Fig> })}</span></>}
              </>
            )}
          </span>
        </div>

        {engineDown ? (
          <div className="lc-state lc-state--broken" style={{ marginTop: 0 }}>
            <h3>
              {tp(AUTO_CONDITION_LABELS[eng.gate].length + MANUAL_CONDITION_LABELS[eng.gate].length, '{n} condition, no values', '{n} conditions, no values')}
            </h3>
            <p>
              {tx(t, 'The labels are code-level constants and are shown because they are real. Their values come from {source}, which answered: “{message}”. Nothing was written. Forcing still works — it records a reason, not a gate reading.', {
                source: <Ref>{state.source}</Ref>,
                message: gateProblem?.message ?? t('no answer'),
              })}
            </p>
            <div className="lc-list" style={{ marginBottom: 12 }}>
              {[...AUTO_CONDITION_LABELS[eng.gate], ...MANUAL_CONDITION_LABELS[eng.gate]].map((label, i) => (
                <div className="lc-cond" key={label}>
                  <div className="lc-mark lc-mark--quiet" title={t('not evaluated')}>?</div>
                  <div>
                    <div className="lc-clabel">{t(label)}</div>
                    <div className="lc-cdetail">
                      <span className="n-unknown" title={t('the gate engine did not answer')}>{t('value unknown')}</span>
                      {' · '}{i < AUTO_CONDITION_LABELS[eng.gate].length ? t('automatic') : t('manual')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="btn" onClick={() => void refresh()}>{t('Retry')}</button>
          </div>
        ) : (
          <>
            <div className="lc-block">
              <span className="u-label">{t('Outstanding')}</span>
              <span className="lc-hint">
                {openConds.length
                  ? tx(t, 'what stands between {code} and {phase}', {
                    code: <Ref>{eng.code}</Ref>,
                    phase: nextPhase ? tPhase(t, crm.PHASE_LABELS[nextPhase]) : t('closure'),
                  })
                  : t('nothing')}
              </span>
            </div>
            <div className="lc-list">
              {openConds.length ? (
                openConds.map((c) => renderCond(c, eng.gate))
              ) : (
                <div className="lc-cond lc-cond--met">
                  <div className="lc-mark lc-mark--auto">▪</div>
                  <div>
                    <div className="lc-clabel">{t('Nothing outstanding.')}</div>
                    <div className="lc-cdetail">
                      {tx(t, 'Every condition on {gate} is held. The advance control above is live.', { gate: <Ref>{eng.gate}</Ref> })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="lc-block">
              <span className="u-label">{tCtx(t, 'Held', 'conditions')}</span>
              {restConds.length ? (
                <button type="button" className="lc-disc" onClick={() => setShowHeld((v) => !v)}>
                  {showHeld ? t('collapse') : tp(restConds.length, 'show the {n} condition already held', 'show the {n} conditions already held')}
                </button>
              ) : (
                <span className="lc-hint">{t('nothing on this gate is held yet')}</span>
              )}
            </div>
            <div className="lc-list">
              {showHeld
                ? restConds.map((c) => renderCond(c, eng.gate))
                : restConds.map((c) => {
                    const st = classify(c);
                    return (
                      <div className="lc-heldrow" key={c.label}>
                        <div className={`lc-mark ${st === 'na' ? 'lc-mark--na' : c.kind === 'manual' ? 'lc-mark--held' : 'lc-mark--auto'}`}>
                          {st === 'na' ? '—' : '▪'}
                        </div>
                        <div className="t">{t(c.label)}</div>
                        <div className="d" title={c.note ?? c.detail}>
                          {st === 'na' ? c.vacuous_reason : c.note ?? c.detail}
                        </div>
                      </div>
                    );
                  })}
              <div className="lc-addrow">
                <input
                  className="form-input"
                  placeholder={t('Add a condition to {gate}…', { gate: eng.gate })}
                  value={newItemLabel}
                  onChange={(e) => setNewItemLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void addManual(eng.gate); }}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={!newItemLabel.trim()}
                  onClick={() => void addManual(eng.gate)}
                >
                  {t('Add')}
                </button>
                <span className="lc-kind">{t('standard conditions cannot be removed — untick them')}</span>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── E · What is ahead ── */}
      {aheadGates.length > 0 && (
        <section className="zone lc-sec" id="ahead">
          <div className="zone-head">
            <h2 className="u-label">{t('What is ahead')}</h2>
            <span className="zone-note zone-note--end lc-tally">{t('read-only · nothing here can be ticked yet')}</span>
          </div>
          <div className="lc-ahead">{aheadGates.map(panelFor)}</div>
          {aheadNa > 0 && (
            <div className="lc-note-na">
              {tx(t, '{n} conditions ahead read as satisfied only because nothing has been recorded against them yet (no defect, no change request, no acceptance record). They are shown as {dash}, not as held, so a far gate cannot look nearer than a near one.', {
                n: <Fig>{aheadNa}</Fig>,
                dash: <span className="n-none">—</span>,
              })}
            </div>
          )}
        </section>
      )}

      {/* ── E′ · What is behind — inspecting a crossed gate, kept ── */}
      {behindGates.length > 0 && (
        <section className="zone lc-sec" id="behind">
          <div className="zone-head">
            <h2 className="u-label">{t('What is behind')}</h2>
            <button type="button" className="lc-disc" onClick={() => setShowBehind((v) => !v)}>
              {showBehind ? t('collapse') : tp(behindGates.length, 'show the {n} gate already crossed', 'show the {n} gates already crossed')}
            </button>
            <span className="zone-note zone-note--end lc-tally">
              {behindOwed
                ? tx(t, '{n} skipped at a forced crossing, still unmet', { n: <Fig>{behindOwed}</Fig> })
                : t('nothing left owing')}
            </span>
          </div>
          {showBehind && <div className="lc-ahead">{behindGates.map(panelFor)}</div>}
        </section>
      )}

      {/* ── F · The record ── */}
      <section className="zone lc-sec" id="record">
        <div className="zone-head">
          <h2 className="u-label">{t('The record')}</h2>
          <span className="zone-note zone-note--end lc-tally">
            {tx(t, state.transitions.length === 1 ? '{n} transition · append-only' : '{n} transitions · append-only', { n: <Fig>{state.transitions.length}</Fig> })}
          </span>
        </div>
        {state.transitions.length ? (
          <div className="lc-record">
            {state.transitions.map((tr) => {
              const from = tPhase(t, crm.PHASE_LABELS[tr.from_phase] ?? tr.from_phase);
              const to = tPhase(t, crm.PHASE_LABELS[tr.to_phase] ?? tr.to_phase);
              const back = crm.PHASE_ORDER.indexOf(tr.to_phase) < crm.PHASE_ORDER.indexOf(tr.from_phase);
              const snap = tr.gate_snapshot;
              const stillOwed = (debt.perTransition.get(tr.id) ?? []).filter((r) => r.still.length);
              return (
                <div className="lc-trow" key={tr.id}>
                  <div style={{ minWidth: 0 }}>
                    <div className="lc-move">
                      <span>{from} → {to}</span>
                      {tr.forced && <span className="lc-badge lc-badge--attend">{t('forced')}</span>}
                      {back && <span className="lc-badge">{t('back')}</span>}
                    </div>
                    {snap && (
                      <div className="lc-snap">
                        {tx(t, '{gate} stood at {tally}', {
                          gate: <Ref>{snap.gate}</Ref>,
                          tally: <Fig>{t('{met} of {total}', { met: snap.metCount, total: snap.total })}</Fig>,
                        })}
                        {snap.metCount === snap.total
                          ? <> — {t('clear at the time')}</>
                          : <> — {tx(t, '{n} unmet at the time', { n: <Fig>{snap.total - snap.metCount}</Fig> })}</>}
                      </div>
                    )}
                    {tr.override_reason && <div className="lc-reason voice-record">{tr.override_reason}</div>}
                    {stillOwed.map((r) => (
                      <div className="lc-debt" key={r.gate}>
                        {tx(t, r.total === 1
                          ? '{still} of the {total} condition skipped on {gate} at this crossing is still unmet: {list}.'
                          : r.still.length === 1
                            ? '{still} of the {total} conditions skipped on {gate} at this crossing is still unmet: {list}.'
                            : '{still} of the {total} conditions skipped on {gate} at this crossing are still unmet: {list}.', {
                          still: <Fig>{r.still.length}</Fig>,
                          total: <Fig>{r.total}</Fig>,
                          gate: <Ref>{r.gate}</Ref>,
                          list: r.still.map((l) => t(l)).join(' · '),
                        })}
                      </div>
                    ))}
                  </div>
                  <div className="lc-when">
                    <Fig>{fmtDateTime(tr.created_at)}</Fig>
                    <div>{tr.actor_name ?? t('system')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            kind="empty"
            title={t('No transition has been recorded')}
            detail={tx(t, 'The engagement has not left its opening phase. A row lands in {table} the moment it does.', { table: <Ref>phase_transitions</Ref> })}
          />
        )}
        <div className="lc-foot">
          {tx(t, 'Document classification, uploads, portal previews and proposal decisions are {not} here — they are the audit log, and they live on {link}. This section carries only what moved the engagement. Every figure above comes from {source}.', {
            not: <b>{t('not')}</b>,
            link: (
              <button
                type="button"
                className="lc-link"
                onClick={() => void openRoute(ROUTES.admin({ tab: 'audit', engagement: eng.id }))}
              >
                {t('Admin · audit')}
              </button>
            ),
            source: <Ref>{state.source}</Ref>,
          })}
        </div>
      </section>

      {/* ── The engagement's own vocabulary, and the health of the translation layer that carries
             it. It belongs on the lifecycle console for the same reason the correspondence
             language does: it is a standing decision about the engagement, not a page action. ── */}
      <TerminologyPanel engagementId={eng.id} />

      {/* ── Step back ── */}
      {backOpen && (
        <div className="modal-overlay" onClick={() => setBackOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Step back')}</h2>
              <button className="close-btn" onClick={() => setBackOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-ink-2" style={{ lineHeight: 1.55, marginTop: 0 }}>
                {tx(t, 'Backward moves are always permitted and always recorded — a backward move crosses no gate. Pick where {code} goes.', { code: <Ref>{eng.code}</Ref> })}
              </p>
              <div className="lc-list">
                {backPhases.map((p) => (
                  <div className="lc-cond" key={p}>
                    <div className="lc-mark lc-mark--quiet">◇</div>
                    <div>
                      <div className="lc-clabel">{tPhase(t, crm.PHASE_LABELS[p])}</div>
                      <div className="lc-routes">
                        <button
                          type="button"
                          className="lc-route"
                          disabled={busy}
                          onClick={() => void doTransition(p)}
                        >
                          {t('Move back to {phase} →', { phase: tPhase(t, crm.PHASE_LABELS[p]) })}
                        </button>
                        <span className="lc-kind">{t('guarded by {gate} on the way out again', { gate: crm.PHASE_GATES[p] })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setBackOpen(false)}>{t('Cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Force override — deliberately heavyweight ── */}
      {forceTarget && (
        <div className="modal-overlay" onClick={() => setForceTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{tx(t, 'Force past {gate}', { gate: <Ref>{eng.gate}</Ref> })}</h2>
              <button className="close-btn" onClick={() => setForceTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ borderLeft: '2px solid var(--attend)', paddingLeft: 14, marginBottom: 16 }}>
                <div className="u-label" style={{ color: 'var(--attend)' }}>{t('Override on the permanent record')}</div>
                <p className="text-sm text-ink-1" style={{ lineHeight: 1.55, margin: '6px 0 0' }}>
                  {tx(t, openConds.length === 1
                    ? 'Moving {code} from {from} to {to} with {n} condition unmet. The reason below is written to the phase transition, snapshot-stamped with the gate state, and read back in the record forever.'
                    : 'Moving {code} from {from} to {to} with {n} conditions unmet. The reason below is written to the phase transition, snapshot-stamped with the gate state, and read back in the record forever.', {
                    code: <Ref>{eng.code}</Ref>,
                    from: <b>{here}</b>,
                    to: <b>{tPhase(t, crm.PHASE_LABELS[forceTarget])}</b>,
                    n: <Fig>{openConds.length}</Fig>,
                  })}
                </p>
              </div>

              <div className="u-label" style={{ marginBottom: 8 }}>{t('What the snapshot will say is unmet')}</div>
              <div className="lc-list">
                {openConds.map((c) => (
                  <div className="lc-cond" key={c.label}>
                    <div className="lc-mark lc-mark--unheld">◇</div>
                    <div>
                      <div className="lc-clabel">{t(c.label)}</div>
                      <div className="lc-cdetail lc-cdetail--open">
                        {c.kind === 'auto' ? c.detail : t('manual · not ticked')}
                      </div>
                      <Subjects c={c} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="lc-cdetail" style={{ margin: '14px 0 6px' }}>
                {t('These become the engagement’s debt: the record will show them as skipped, and this page will keep saying so until they are met.')}
              </div>

              <div className="u-label" style={{ margin: '16px 0 6px' }}>
                {t('Override reason')}{' '}
                <span className="lc-hint" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                  {t('— your words, quoted back verbatim')}
                </span>
              </div>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder={t('Why is this move justified despite the gate?')}
                value={forceReason}
                onChange={(e) => setForceReason(e.target.value)}
              />
              <label className="flex items-start gap-8 text-sm text-ink-2 mt-12">
                <input type="checkbox" checked={forceAck} onChange={(e) => setForceAck(e.target.checked)} />
                <span>{t('I accept that this override is permanent and visible in the engagement record.')}</span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setForceTarget(null)}>{t('Cancel')}</button>
              <button
                className="btn btn-danger"
                disabled={busy || !forceAck || forceReason.trim().length < 5}
                onClick={() => void doTransition(forceTarget, true, forceReason.trim())}
              >
                {t('Force to {phase}', { phase: tPhase(t, crm.PHASE_LABELS[forceTarget]) })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── The engagement record ── */}
      {editOpen && (
        <div className="modal-overlay" onClick={() => setEditOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{tx(t, 'Engagement record — {code}', { code: <Ref>{eng.code}</Ref> })}</h2>
              <button className="close-btn" onClick={() => setEditOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="eng-name">{t('Name')}</label>
                <input
                  id="eng-name" className="form-input" value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>

              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label" htmlFor="eng-model">{t('Commercial model')}</label>
                  <select
                    id="eng-model" className="form-select" value={editForm.commercial_model}
                    onChange={(e) => setEditForm({ ...editForm, commercial_model: e.target.value })}
                  >
                    {Object.entries(crm.COMMERCIAL_MODEL_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{t(v)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ width: 110 }}>
                  <label className="form-label" htmlFor="eng-ccy">{t('Currency')}</label>
                  <input
                    id="eng-ccy" className="form-input mono" maxLength={3} value={editForm.currency}
                    onChange={(e) => setEditForm({ ...editForm, currency: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="form-group" style={{ width: 130 }}>
                  <label className="form-label" htmlFor="eng-lang" title={t('Inherited from the client; everything produced inside this engagement is written in it.')}>{t('Language')}</label>
                  <select
                    id="eng-lang" className="form-select" value={editForm.language}
                    onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}
                  >
                    {LANGUAGES.map((l) => <option key={l} value={l}>{LANGUAGE_LABEL[l]}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ width: 130 }}>
                  <label className="form-label" htmlFor="eng-window">{t('Review window (d)')}</label>
                  <input
                    id="eng-window" className="form-input mono" type="number" min={0} max={365}
                    value={editForm.review_window_days}
                    onChange={(e) => setEditForm({ ...editForm, review_window_days: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label" htmlFor="eng-sponsor">{t('Sponsor')}</label>
                  <select
                    id="eng-sponsor" className="form-select" value={editForm.sponsor_contact_id}
                    onChange={(e) => setEditForm({ ...editForm, sponsor_contact_id: e.target.value })}
                  >
                    <option value="">{contacts.length ? t('not named yet') : t('no contacts on this client')}</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.role_title ? ` · ${c.role_title}` : ''}</option>
                    ))}
                  </select>
                  <div className="text-xs text-ink-3 mt-4">
                    {tx(t, 'Signs the SOW and acceptance records; clears the counterpart condition on {gate}.', { gate: <Ref>G0</Ref> })}
                  </div>
                </div>
                <div className="form-group" style={{ width: 170 }}>
                  <label className="form-label" htmlFor="eng-estimate">{t('Estimate ({currency})', { currency: eng.currency })}</label>
                  <input
                    id="eng-estimate" className="form-input mono" type="number" min={0} step="1000"
                    placeholder="45000" value={editForm.estimate_value}
                    onChange={(e) => setEditForm({ ...editForm, estimate_value: e.target.value })}
                  />
                  <div className="text-xs text-ink-3 mt-4">{tx(t, 'Satisfies {gate} until milestones are priced.', { gate: <Ref>G2</Ref> })}</div>
                </div>
              </div>

              <div className="flex gap-12">
                <div className="form-group" style={{ width: 180 }}>
                  <label className="form-label" htmlFor="eng-kickoff">{t('Kickoff date')}</label>
                  <input
                    id="eng-kickoff" className="form-input mono" type="date" value={editForm.kickoff_date}
                    onChange={(e) => setEditForm({ ...editForm, kickoff_date: e.target.value })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label" htmlFor="eng-parent">{t('Parent engagement')}</label>
                  <select
                    id="eng-parent" className="form-select" value={editForm.parent_engagement_id}
                    onChange={(e) => setEditForm({ ...editForm, parent_engagement_id: e.target.value })}
                  >
                    <option value="">{t('none')}</option>
                    {engagements
                      .filter((x) => x.id !== eng.id && x.client_id === eng.client_id)
                      .map((x) => <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="text-xs text-ink-3" style={{ lineHeight: 1.5 }}>
                {tx(t, 'The code {code} is immutable — it is baked into refs and the agent workspace. Gate {gate} reads the kickoff date; the review window drives deemed-acceptance timers on every acceptance record issued from now on.', {
                  code: <Ref>{eng.code}</Ref>,
                  gate: <Ref>G4</Ref>,
                })}
              </div>
            </div>
            <div className="modal-footer">
              {eng.archived_at ? (
                <button className="btn" style={{ marginRight: 'auto' }} disabled={busy} onClick={() => void setArchived(false)}>
                  {t('Restore')}
                </button>
              ) : (
                <button
                  className="btn btn-danger" style={{ marginRight: 'auto' }} disabled={busy}
                  onClick={() => setArchiveConfirm(true)}
                >
                  {t('Archive')}
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => setEditOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={busy || !editForm.name.trim()} onClick={() => void saveEngagement()}>
                {busy ? t('Saving…') : t('Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={archiveConfirm}
        title={t('Archive engagement')}
        confirmLabel={t('Archive')}
        danger
        onCancel={() => setArchiveConfirm(false)}
        onConfirm={() => void setArchived(true)}
      >
        <p className="text-sm text-ink-2">
          {tx(t, 'Archive {code}? Nothing is deleted — the engagement drops out of the active list and its portal, register and history stay exactly as they are. You can restore it from this dialog.', { code: <Ref>{eng.code}</Ref> })}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={!!removeItem}
        title={t('Remove gate condition')}
        confirmLabel={t('Remove')}
        danger
        onCancel={() => setRemoveItem(null)}
        onConfirm={() => void confirmRemoveItem()}
      >
        <p className="text-sm text-ink-2">
          {tx(t, 'Remove the custom condition “{label}” from gate {gate}? Standard conditions cannot be removed — untick them instead.', {
            label: removeItem?.label,
            gate: <Ref>{eng.gate}</Ref>,
          })}
        </p>
      </ConfirmModal>
    </div>
  );
}
